// Cabinet Vision CSV importer format.
//
// Cabinet Vision's "Job Import" wizard (Solid v15+) accepts a pair of CSVs:
//   • parts.csv     — one row per cut panel
//   • hardware.csv  — one row per consumed item (hinges, slides, …)
// Both files share an Item-Number column that links to the parent cabinet
// so CV can rebuild the BOM grouping after import.
//
// The CSV uses a CRLF line terminator and CP-1252-friendly quoting so it
// pastes cleanly into Solid even from Notepad.

import type { Cabinet, CabinetPart } from "@/lib/types";
import { isDoorPart } from "@/lib/cabinets/roles";
import type { ExportArtifact, ExportContext, Postprocessor } from "./types";
import { makeZip } from "./zip";

// ── CSV plumbing ─────────────────────────────────────────────────

function csvCell(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = typeof v === "boolean" ? (v ? "Y" : "N") : String(v);
  // RFC 4180: quote when value contains comma, quote, or newline. Escape
  // embedded quotes by doubling.
  if (/[,"\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells: Array<string | number | boolean | null | undefined>): string {
  return cells.map(csvCell).join(",");
}

function fix(n: number, places = 4): string {
  return Number.isFinite(n) ? n.toFixed(places) : "0";
}

// ── Sheet builders ──────────────────────────────────────────────

const PART_HEADER = [
  "Job",           // project name (denormalised on every row so CV groups
                   // multiple jobs in one batch)
  "ItemNumber",    // matches the cabinet's _id
  "ItemName",      // cabinet class label
  "PartNumber",    // running 1..N within an item
  "PartName",
  "Length",        // grain-aligned dimension (inches)
  "Width",         // cross-grain
  "Thickness",
  "Quantity",
  "Material",
  "Grain",         // length / width / none
  "EdgeTop",
  "EdgeBottom",
  "EdgeLeft",
  "EdgeRight",
];

const HARDWARE_HEADER = [
  "Job",
  "ItemNumber",
  "HardwareID",
  "Kind",          // hinge / drawer_slide / pull / knob / leg / shelf_pin / fastener
  "Brand",
  "Model",
  "Finish",
  "Quantity",
  "CostEach",
];

function partsCsv(ctx: ExportContext): string {
  const rows: string[] = [PART_HEADER.join(",")];
  for (const { cabinet, parts } of ctx.parts) {
    parts.forEach((p, idx) => rows.push(buildPartRow(ctx, cabinet, p, idx + 1)));
  }
  return rows.join("\r\n") + "\r\n";
}

function buildPartRow(
  ctx: ExportContext,
  cabinet: Cabinet,
  p: CabinetPart,
  partNumber: number,
): string {
  const material = ctx.catalog.materials.get(p.materialId);
  const edgeName = (id: number | null) =>
    id == null ? "" : (ctx.catalog.edges.get(id)?.name ?? `edge_${id}`);
  return csvRow([
    ctx.project.name,
    cabinet._id,
    cabinet.classId,
    partNumber,
    p.name,
    fix(p.w),
    fix(p.h),
    fix(p.thickness),
    p.qty ?? 1,
    material?.name ?? "",
    p.grain,
    edgeName(p.edges.t),
    edgeName(p.edges.b),
    edgeName(p.edges.l),
    edgeName(p.edges.r),
  ]);
}

/** Hardware aggregated at the project level (ItemNumber column is left
 *  blank). Per-cabinet hardware allocation isn't tracked in ProjectBom
 *  today; users group/filter in CV after import. */
function hardwareCsv(ctx: ExportContext): string {
  const rows: string[] = [HARDWARE_HEADER.join(",")];
  for (const row of ctx.bom.hardware) {
    const h = ctx.catalog.hardware.get(row.hardwareId);
    rows.push(csvRow([
      ctx.project.name,
      "",                                // project-wide row
      row.hardwareId,
      row.kind,
      h?.brand ?? "",
      h?.model ?? "",
      h?.finish ?? "",
      row.qty,
      row.unitCost,
    ]));
  }
  return rows.join("\r\n") + "\r\n";
}

// ── Postprocessor ────────────────────────────────────────────────

const cabinetVisionCsv: Postprocessor = {
  id: "cabinet_vision_csv",
  label: "Cabinet Vision (CSV import)",
  description: "Two-file CSV bundle (parts + hardware) for Cabinet Vision Solid's Job Import. Inches, RFC-4180 CSV, CRLF.",
  category: "machine",
  extension: "csv",
  options: [
    {
      key: "split_files",
      label: "Emit separate parts.csv / hardware.csv",
      type: "boolean",
      default: true,
      help: "Off = one combined CSV with a blank-line separator (rare; for legacy CV4 imports).",
    },
    {
      key: "skip_doors",
      label: "Skip door / face panels",
      type: "boolean",
      default: false,
      help: "Doors are often imported through CV's separate Door Library; turn this on if your shop maintains them elsewhere.",
    },
  ],
  generate(ctx, opts): ExportArtifact[] {
    // Optional door filter — operate on a shallow-cloned ctx so the
    // original isn't mutated for downstream postprocessors.
    const filteredCtx: ExportContext = opts.skip_doors
      ? {
          ...ctx,
          parts: ctx.parts.map((g) => ({
            cabinet: g.cabinet,
            parts: g.parts.filter((p) => !isDoorPart(p)),
          })),
        }
      : ctx;

    const partsText = partsCsv(filteredCtx);
    const hardwareText = hardwareCsv(filteredCtx);
    const slug = filteredCtx.project.name.replace(/[^\w]+/g, "_").replace(/^_+|_+$/g, "") || "cv_export";

    if (opts.split_files) {
      const zip = makeZip([
        { name: `${slug}/parts.csv`,    content: partsText },
        { name: `${slug}/hardware.csv`, content: hardwareText },
      ]);
      return [{
        filename: `${slug}_cv.zip`,
        content: zip,
        mimeType: "application/zip",
      }];
    }

    // Combined single-file mode: parts then a blank line then hardware.
    return [{
      filename: `${slug}_cv.csv`,
      content: partsText + "\r\n" + hardwareText,
      mimeType: "text/csv",
    }];
  },
};

export default cabinetVisionCsv;
